import type { DicomJson, SeriesRef, StudyRef } from '../dicomweb';
import { tagString } from '../dicomweb';
import type { ViewerShell } from '../shell';
import { loadSeries } from '../study';

const SERIES_UID_TAG = '0020000E';

/** The study of what the active cell shows (image stack or PDF / video / SR), else the opened study. */
export function activeStudyUID(shell: ViewerShell): string {
  const vp = shell.activeViewport();
  const series = vp?.currentStack?.series ?? vp?.customSeries;
  return series?.studyInstanceUID ?? shell.study().studyInstanceUID;
}

export function studyByUID(shell: ViewerShell, uid: string): StudyRef | undefined {
  return shell.studies().find((s) => s.studyInstanceUID === uid);
}

/**
 * Adds the series the PACS has for `studyUID` that the browser does not list yet (after a capture or
 * an attached PDF), by QIDO on the study and metadata for the new series only. Returns the new series.
 */
export async function refreshStudySeries(shell: ViewerShell, studyUID: string): Promise<SeriesRef[]> {
  const study = studyByUID(shell, studyUID);
  if (!study) return [];
  const client = shell.ctx.client;
  const rows: DicomJson[] = await client.listSeries(studyUID);
  const known = new Set(study.series.map((s) => s.seriesInstanceUID));
  const fresh = rows.filter((row) => {
    const uid = tagString(row, SERIES_UID_TAG);
    return uid && !known.has(uid);
  });
  const added = (await Promise.all(fresh.map((row) => loadSeries(client, studyUID, row, shell.ctx.mode)))).filter((s) => s.instances.length > 0);
  if (added.length === 0) return [];
  study.series.push(...added);
  study.series.sort((a, b) => a.seriesNumber - b.seriesNumber);
  shell.emit('studies-changed', { studies: shell.studies() });
  return added;
}

/** Saves a blob through a temporary download link. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
